import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";

export default function NavBar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const active = "text-[var(--aa-text)]";
  const idle = "text-[var(--aa-text-3)] hover:text-[var(--aa-text)] transition-colors duration-300";

  const close = () => setOpen(false);

  const doLogout = async () => {
    close();
    await logout();
    nav("/");
  };

  return (
    <header className="sticky top-0 z-30 backdrop-blur-md bg-[rgba(253,251,247,0.85)] border-b border-[var(--aa-elevated)]">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
        <Link to="/" data-testid="nav-home" onClick={close} className="font-display text-xl tracking-tight">
          Ageless<span className="serif-italic"> Alchemy</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-7 text-sm">
          {user ? (
            <>
              <NavLink to="/dashboard" data-testid="nav-dashboard" className={({ isActive }) => (isActive ? active : idle)}>Dashboard</NavLink>
              <NavLink to="/protocol" data-testid="nav-protocol" className={({ isActive }) => (isActive ? active : idle)}>My Protocol</NavLink>
              <NavLink to="/analyze" data-testid="nav-analyze" className={({ isActive }) => (isActive ? active : idle)}>Analyze</NavLink>
              <NavLink to="/library" data-testid="nav-library" className={({ isActive }) => (isActive ? active : idle)}>Library</NavLink>
              <NavLink to="/tracker" data-testid="nav-tracker" className={({ isActive }) => (isActive ? active : idle)}>Tracker</NavLink>
              <button
                data-testid="nav-logout"
                onClick={doLogout}
                className="aa-pill hover:bg-[var(--aa-elevated)] transition-colors duration-300"
              >Log out</button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className={idle}>Log in</Link>
              <Link to="/register" data-testid="nav-register" className="aa-pill bg-[var(--aa-text)] text-[var(--aa-bg)] border-[var(--aa-text)] hover:opacity-90">Create account</Link>
            </>
          )}
        </nav>

        {/* Mobile hamburger */}
        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          aria-expanded={open}
          className="md:hidden p-2 -mr-2"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
            {open ? (
              <>
                <line x1="5" y1="5" x2="19" y2="19" />
                <line x1="19" y1="5" x2="5" y2="19" />
              </>
            ) : (
              <>
                <line x1="3" y1="7" x2="21" y2="7" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="17" x2="21" y2="17" />
              </>
            )}
          </svg>
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden border-t border-[var(--aa-elevated)] bg-[var(--aa-bg)]" data-testid="nav-mobile-drawer">
          <nav className="px-6 py-4 flex flex-col gap-1 text-base">
            {user ? (
              <>
                <NavLink to="/dashboard" onClick={close} data-testid="nav-mobile-dashboard" className={({ isActive }) => `py-3 ${isActive ? active : idle}`}>Dashboard</NavLink>
                <NavLink to="/protocol" onClick={close} data-testid="nav-mobile-protocol" className={({ isActive }) => `py-3 ${isActive ? active : idle}`}>My Protocol</NavLink>
                <NavLink to="/analyze" onClick={close} data-testid="nav-mobile-analyze" className={({ isActive }) => `py-3 ${isActive ? active : idle}`}>Analyze</NavLink>
                <NavLink to="/library" onClick={close} data-testid="nav-mobile-library" className={({ isActive }) => `py-3 ${isActive ? active : idle}`}>Library</NavLink>
                <NavLink to="/tracker" onClick={close} data-testid="nav-mobile-tracker" className={({ isActive }) => `py-3 ${isActive ? active : idle}`}>Tracker</NavLink>
                <NavLink to="/how-it-works" onClick={close} data-testid="nav-mobile-how" className={({ isActive }) => `py-3 ${isActive ? active : idle}`}>The SMART Method</NavLink>
                <NavLink to="/application-tips" onClick={close} data-testid="nav-mobile-tips" className={({ isActive }) => `py-3 ${isActive ? active : idle}`}>Application tips</NavLink>
                <NavLink to="/summary" onClick={close} data-testid="nav-mobile-summary" className={({ isActive }) => `py-3 ${isActive ? active : idle}`}>Print Summary</NavLink>
                <button
                  data-testid="nav-mobile-logout"
                  onClick={doLogout}
                  className="mt-2 mb-3 py-3 text-left text-[var(--aa-text-2)] border-t border-[var(--aa-elevated)]"
                >Log out</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={close} data-testid="nav-mobile-login" className={`py-3 ${idle}`}>Log in</Link>
                <Link to="/register" onClick={close} data-testid="nav-mobile-register" className="py-3 text-[var(--aa-text)] font-medium">Create account →</Link>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
